/**
 * Duration Utilities
 *
 * Converts Tone.js-style duration strings to seconds without requiring Tone.js.
 * This is used by the AudioScheduler-based playback system.
 */

/**
 * Convert a Tone.js-style duration string to seconds
 *
 * Supported formats:
 * - Note values: "1n", "2n", "4n", "8n", "16n", "32n" (whole, half, quarter, eighth, etc.)
 * - Dotted notes: "4n." (adds 50% to duration)
 * - Triplets: "4t" (2/3 of the note value)
 * - Numeric: "0.5" or 0.5 (direct seconds)
 * - Time notation: "1:2:3" (bars:beats:sixteenths)
 *
 * @param duration - Duration string or number
 * @param bpm - Beats per minute (default 120)
 * @returns Duration in seconds
 */
export function durationToSeconds(duration: string | number, bpm = 120): number {
  // If already a number, return as-is (assumed to be seconds)
  if (typeof duration === 'number') {
    return duration;
  }

  const quarterNoteSeconds = 60 / bpm;

  // Handle numeric strings
  if (/^\d+(\.\d+)?$/.test(duration)) {
    return parseFloat(duration);
  }

  // Handle time notation "bars:beats:sixteenths"
  if (duration.includes(':')) {
    const parts = duration.split(':').map(Number);
    const bars = parts[0] || 0;
    const beats = parts[1] || 0;
    const sixteenths = parts[2] || 0;

    return (bars * 4 + beats + sixteenths / 4) * quarterNoteSeconds;
  }

  // Handle note values (1n, 2n, 4n, 8n, 16n, 32n, etc.)
  const noteMatch = duration.match(/^(\d+)(n|t)(\.)?$/);
  if (noteMatch) {
    const noteValue = parseInt(noteMatch[1], 10);
    const isTriplet = noteMatch[2] === 't';
    const isDotted = noteMatch[3] === '.';

    // Calculate base duration (relative to quarter note)
    // 1n = 4 quarter notes, 2n = 2 quarter notes, 4n = 1 quarter note, 8n = 0.5 quarter notes
    let noteDuration = (4 / noteValue) * quarterNoteSeconds;

    // Triplet: 2/3 of the note value
    if (isTriplet) {
      noteDuration *= 2 / 3;
    }

    // Dotted: adds 50% to duration
    if (isDotted) {
      noteDuration *= 1.5;
    }

    return noteDuration;
  }

  // Handle simple measure notation like "1m" (1 measure = 4 beats in 4/4)
  const measureMatch = duration.match(/^(\d+)m$/);
  if (measureMatch) {
    const measures = parseInt(measureMatch[1], 10);
    return measures * 4 * quarterNoteSeconds;
  }

  // Unknown format - log warning and return quarter note as fallback
  console.warn(`Unknown duration format: "${duration}", using quarter note as fallback`);
  return quarterNoteSeconds;
}

/**
 * Convert seconds to a Tone.js-style duration string
 * This is approximate and will round to the nearest standard note value
 *
 * @param seconds - Duration in seconds
 * @param bpm - Beats per minute (default 120)
 * @returns Approximate duration string
 */
export function secondsToDuration(seconds: number, bpm = 120): string {
  const quarterNoteSeconds = 60 / bpm;
  const quarterNotes = seconds / quarterNoteSeconds;

  // Map to standard note values
  const noteValues: [number, string][] = [
    [4, '1n'],     // whole note
    [3, '2n.'],    // dotted half
    [2, '2n'],     // half note
    [1.5, '4n.'],  // dotted quarter
    [1, '4n'],     // quarter note
    [0.75, '8n.'], // dotted eighth
    [0.5, '8n'],   // eighth note
    [0.375, '16n.'], // dotted sixteenth
    [0.25, '16n'],  // sixteenth note
    [0.125, '32n'], // thirty-second note
  ];

  // Find closest match
  let closestMatch = '4n';
  let closestDiff = Infinity;

  for (const [noteQuarters, noteName] of noteValues) {
    const diff = Math.abs(quarterNotes - noteQuarters);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestMatch = noteName;
    }
  }

  return closestMatch;
}

/**
 * Convert MIDI note number to frequency (Hz)
 *
 * @param midiNote - MIDI note number (0-127)
 * @returns Frequency in Hz
 */
export function midiNoteToFrequency(midiNote: number): number {
  // A4 (MIDI 69) = 440 Hz
  return 440 * Math.pow(2, (midiNote - 69) / 12);
}

/**
 * Convert note name to MIDI note number
 *
 * @param noteName - Note name like "C4", "F#3", "Bb5"
 * @returns MIDI note number
 */
export function noteNameToMidi(noteName: string): number {
  // Normalize note name
  const normalized = noteName.replace('♯', '#').replace('♭', 'b');

  const match = normalized.match(/^([A-Ga-g])([#b]?)(-?\d+)$/);
  if (!match) {
    console.warn(`Invalid note name: "${noteName}"`);
    return 60; // Middle C as fallback
  }

  const [, letter, accidental, octaveStr] = match;
  const octave = parseInt(octaveStr, 10);

  // Note values relative to C
  const noteValues: Record<string, number> = {
    'C': 0, 'D': 2, 'E': 4, 'F': 5, 'G': 7, 'A': 9, 'B': 11,
    'c': 0, 'd': 2, 'e': 4, 'f': 5, 'g': 7, 'a': 9, 'b': 11,
  };

  let semitone = noteValues[letter] ?? 0;

  if (accidental === '#') {
    semitone += 1;
  } else if (accidental === 'b') {
    semitone -= 1;
  }

  // MIDI note = (octave + 1) * 12 + semitone
  // C4 = (4 + 1) * 12 + 0 = 60
  return (octave + 1) * 12 + semitone;
}

/**
 * Convert MIDI note number to note name
 *
 * @param midiNote - MIDI note number (0-127)
 * @returns Note name like "C4", "F#3"
 */
export function midiToNoteName(midiNote: number): string {
  const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const octave = Math.floor(midiNote / 12) - 1;
  const note = noteNames[midiNote % 12];
  return `${note}${octave}`;
}
